import { gql } from '@apollo/client'
import { sessionFieldsQuery } from '../../queries/getSession/query'

/**
 * `identifyAndCreateSession` mutation.
 *
 * The orchestrating mutation on the user API that COM-1367 ships. It runs
 * `identify` first and unconditionally, then creates the session — so no
 * path reuses an existing session without a fresh identify, closing D1.
 *
 * The variable list is intentionally narrow:
 *
 * - `storefrontName: String!` — the slug cinnamon already carries on
 *   `defaultSlice.storeName`, same shape as every other mutation in this
 *   folder expects it in.
 * - `storeCode: String!` — the only thing the shopper types on the login
 *   screen. `LoginForm` captures it under this name
 *   (`4f37719:src/ui/components/LoginForm/LoginForm.types.ts`), so the
 *   emit arg from the EventManager carries it as-is.
 * - `channel: String` — the webview's channel (`whatsapp`, `line`, …) read
 *   off the mounted session's `workflow.channel` or an equivalent URL
 *   param. Optional on the mutation so a storefront that does not pin a
 *   channel sends nothing.
 *
 * `locale` is **not** declared here: cinnamon's active locale lives on
 * `defaultSlice.config.options.localization` and is already applied through
 * `config.texts`, so the backend never needs it to pick an error message. If
 * COM-1367 publishes a different shape (e.g. a required `channel` or an
 * `inputs` object), the reconcile step in the plan narrows or widens this to
 * match the live schema; the caller signature stays stable because the hook
 * builds the variables object, not the component.
 *
 * The response selects the created session's `id` (becomes `sessionUid`) and
 * the full `sessionFieldsQuery` set, so cinnamon's existing `setSessionId`
 * and `setSessionData` reducers accept the payload unchanged.
 * @internal
 */
const identifyAndCreateSessionMutation = gql`
  mutation IdentifyAndCreateSession(
    $storefrontName: String!
    $storeCode: String!
    $channel: String
  ) {
    identifyAndCreateSession(
      storefrontName: $storefrontName
      storeCode: $storeCode
      channel: $channel
    ) {
      sessionUid
      session {
        ${sessionFieldsQuery}
      }
    }
  }
`

export default identifyAndCreateSessionMutation
